import { Outlet, useRouter } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { Toaster } from "sonner";
import { useGoToHotkey } from "../../hooks/useGoToHotkey";
import { useHotkey } from "../../hooks/useHotkey";
import { useThemeApply } from "../../lib/theme";
import { useUI } from "../../stores/ui";
import { ChatRail } from "../chat/ChatRail";
import { CommandPalette } from "./CommandPalette";
import { Sidebar } from "./Sidebar";
import { TopBreadcrumb } from "./TopBreadcrumb";
import { Topbar } from "./Topbar";

export function AppShell() {
  useThemeApply();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const density = useUI((s) => s.density);
  const readingMode = useUI((s) => s.readingMode);
  const showAnn = useUI((s) => s.showAnnotations);
  const marginChips = useUI((s) => s.marginChips);
  const proseFont = useUI((s) => s.proseFont);
  const theme = useUI((s) => s.theme);

  // uiv2 hotkeys (plan §7):
  // ⌘K command palette · ⌘B sidebar · ⌘I AI panel · ⌘J chat rail
  useHotkey("meta+k", () => setPaletteOpen(true));
  useHotkey("meta+b", () => useUI.getState().toggleSidebar());
  useHotkey("meta+i", () => useUI.getState().toggleAiPanel());
  useHotkey("meta+j", () => useUI.getState().toggleChatPanel());

  // B5.4 — "g X" navigation
  const router = useRouter();
  const goTo = useCallback(
    (path: string) => {
      // Use raw history for paths not in the typed tree to avoid generic friction.
      router.history.push(path);
    },
    [router],
  );
  useGoToHotkey(goTo);

  const bodyClasses = [
    theme === "dark" ? "theme-dark" : "",
    density === "compact" ? "density-compact" : "",
    readingMode ? "reading-mode" : "",
    !showAnn ? "no-annotations" : "",
    marginChips ? "margin-chips-on" : "margin-chips-off",
    `prose-${proseFont}`,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={`flex h-full flex-col bg-(--bg-canvas) ${bodyClasses}`}
      data-testid="app-shell"
      data-theme={theme}
      data-margin-chips={marginChips ? "on" : "off"}
    >
      <Topbar onOpenCommandPalette={() => setPaletteOpen(true)} />
      <TopBreadcrumb />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-y-auto" id="main-content">
          <Outlet />
        </main>
        <ChatRail />
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "var(--bg-overlay)",
            color: "var(--text-primary)",
            border: "1px solid var(--border-subtle)",
          },
        }}
      />
    </div>
  );
}
